import { useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Icon } from '@iconify/react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  MAINTENANCE_TYPES,
  formatMinutes,
  planNextMaintenance,
  type MaintenanceOverview,
  type MaintenanceRecordInput,
} from '@jupi/shared'
import { ApiError, DISPLAY_STYLE, RemoteContent, api, useAuth, useClubToday } from '@jupi/ui'
import { HUOLTO, useOverview } from '../api'
import { HoursField } from '../components/HoursField'

// "Perform maintenance": a single scrolling page, top to bottom, no wizard to
// get lost in. Everything the mechanic would otherwise type is filled in --
// the type from what is due, the hours from the flight log, the next due point
// from those -- and every filled-in figure can be changed. Nothing is saved
// until "Sign and complete" has been confirmed.
export default function PerformPage() {
  const { registration = '' } = useParams()
  const { data, isLoading, error } = useOverview(registration)
  return <RemoteContent isLoading={isLoading} error={error}>{data ? <Form overview={data} /> : null}</RemoteContent>
}

const OTHER = 'other'
const sameType = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()

function Form({ overview: o }: { overview: MaintenanceOverview }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const today = useClubToday()
  const queryClient = useQueryClient()

  // What is due next is what is probably being done now.
  const due = o.schedule?.nextType ?? ''
  const standard = MAINTENANCE_TYPES.find((m) => sameType(m.label, due))
  const [choice, setChoice] = useState<string>(standard ? standard.label : due ? OTHER : '')
  const [custom, setCustom] = useState(standard ? '' : due)
  const [performedOn, setPerformedOn] = useState(today)
  const [airframe, setAirframe] = useState<number | null>(o.airframeMinutes)
  const [parts, setParts] = useState<number[]>([])
  const [remarks, setRemarks] = useState('')
  const [signedBy, setSignedBy] = useState(user?.displayName ?? '')
  const [confirm, setConfirm] = useState(false)

  const maintenanceType = choice === OTHER ? custom.trim() : choice

  // The next maintenance, filled in until the mechanic changes it.
  const plan = planNextMaintenance({
    maintenanceType,
    airframeMinutes: airframe ?? 0,
    performedOn,
    schedule: o.schedule,
  })
  const [dueEdit, setDueEdit] = useState<{ value: number | null } | null>(null)
  const [typeEdit, setTypeEdit] = useState<string | null>(null)
  const [dateEdit, setDateEdit] = useState<string | null>(null)
  const nextDue = dueEdit ? dueEdit.value : plan.nextDueMinutes
  const nextType = typeEdit ?? plan.nextType
  const nextDate = dateEdit ?? plan.nextDueDate ?? ''
  const untouched = !dueEdit && typeEdit == null && dateEdit == null

  const valid = maintenanceType !== '' && airframe != null && nextDue != null && nextType.trim() !== '' && signedBy.trim() !== ''

  const record = useMutation({
    mutationFn: () => {
      const body: MaintenanceRecordInput = {
        performedOn,
        maintenanceType,
        airframeMinutes: airframe ?? 0,
        performedBy: signedBy.trim(),
        notes: remarks.trim() || null,
        next: { ...plan, nextType: nextType.trim(), nextDueMinutes: nextDue ?? plan.nextDueMinutes, nextDueDate: nextDate || null },
        componentsDone: parts,
      }
      return api.post(`/api/maintenance/${encodeURIComponent(o.registration)}/records`, body)
    },
    onSuccess: () => {
      setConfirm(false)
      return queryClient.invalidateQueries({ queryKey: HUOLTO })
    },
    onError: () => setConfirm(false),
  })

  if (record.isSuccess) {
    return (
      <Stack spacing={3} sx={{ alignItems: 'center', textAlign: 'center', py: 6 }}>
        <Icon icon="mdi:check-circle" width={120} color="#2e9e4f" />
        <Typography variant="h3" component="h1">
          {t('huolto.doneTitle')}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {t('huolto.doneText', { registration: o.registration, next: formatMinutes(nextDue ?? 0) })}
        </Typography>
        <Button component={RouterLink} to={`/aircraft/${encodeURIComponent(o.registration)}`} replace variant="contained" size="large" fullWidth>
          {t('huolto.backToAircraft', { registration: o.registration })}
        </Button>
        <Button component={RouterLink} to="/" size="large" fullWidth>
          {t('huolto.backToFleet')}
        </Button>
      </Stack>
    )
  }

  return (
    <Stack spacing={2.5} sx={{ pb: 12 }}>
      <Box>
        <Typography variant="h4" component="h1">
          {t('huolto.performTitle')}
        </Typography>
        <Typography sx={{ ...DISPLAY_STYLE, fontSize: '2rem' }}>{o.registration}</Typography>
      </Box>

      {record.isError ? (
        <Alert severity="error">
          {record.error instanceof ApiError && record.error.status === 0 ? t('huolto.offline') : t('huolto.saveFailed')}
        </Alert>
      ) : null}

      <Step title={t('huolto.stepType')}>
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(3, 1fr)' }}>
          {[...MAINTENANCE_TYPES.map((m) => m.label), OTHER].map((value) => (
            <Button
              key={value}
              size="large"
              variant={choice === value ? 'contained' : 'outlined'}
              aria-pressed={choice === value}
              onClick={() => setChoice(value)}
              sx={{ fontWeight: 700 }}
            >
              {value === OTHER ? t('huolto.otherType') : value}
            </Button>
          ))}
        </Box>
        {choice === OTHER ? (
          <TextField
            label={t('huolto.otherTypeLabel')}
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            autoFocus
            fullWidth
            sx={{ mt: 2 }}
            slotProps={{ htmlInput: { maxLength: 40 } }}
          />
        ) : null}
      </Step>

      <Step title={t('huolto.stepHours')}>
        <Stack spacing={2.5}>
          <HoursField label={t('huolto.airframeHours')} minutes={airframe} onChange={setAirframe} helperText={t('huolto.hoursHelp')} />
          <TextField
            type="date"
            label={t('maintenance.performedOn')}
            value={performedOn}
            onChange={(e) => setPerformedOn(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
      </Step>

      <Step title={t('huolto.stepNext')}>
        <Stack spacing={2.5}>
          {untouched && maintenanceType ? <Alert severity="info" icon={<Icon icon="mdi:auto-fix" />}>{t('huolto.autoFilled')}</Alert> : null}
          <HoursField label={t('huolto.nextDueAt')} minutes={nextDue} onChange={(value) => setDueEdit({ value })} />
          <TextField label={t('huolto.nextType')} value={nextType} onChange={(e) => setTypeEdit(e.target.value)} slotProps={{ htmlInput: { maxLength: 40 } }} />
          <TextField
            type="date"
            label={t('huolto.nextDate')}
            value={nextDate}
            onChange={(e) => setDateEdit(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
      </Step>

      {o.components.length > 0 ? (
        <Step title={t('huolto.stepParts')}>
          <Stack>
            {o.components.map((c) => (
              <FormControlLabel
                key={c.componentId}
                control={
                  <Checkbox
                    checked={parts.includes(c.componentId)}
                    onChange={(e) => setParts((p) => (e.target.checked ? [...p, c.componentId] : p.filter((id) => id !== c.componentId)))}
                    slotProps={{ input: { style: { width: 44, height: 44 } } }}
                    sx={{ '& svg': { fontSize: 36 } }}
                  />
                }
                label={`${c.name} (${t(`huolto.kind_${c.kind}`)})`}
                sx={{ minHeight: 64, mx: 0, '& .MuiFormControlLabel-label': { fontSize: '1.2rem' } }}
              />
            ))}
          </Stack>
        </Step>
      ) : null}

      <Step title={t('huolto.stepRemarks')}>
        <TextField
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          multiline
          minRows={3}
          fullWidth
          slotProps={{ htmlInput: { maxLength: 2000 } }}
        />
      </Step>

      <Step title={t('huolto.stepSign')}>
        <TextField label={t('huolto.performedBy')} value={signedBy} onChange={(e) => setSignedBy(e.target.value)} fullWidth slotProps={{ htmlInput: { maxLength: 200 } }} />
      </Step>

      <Box
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          p: 2,
          pb: 'calc(16px + env(safe-area-inset-bottom))',
          bgcolor: 'background.default',
          borderTop: 1,
          borderColor: 'divider',
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Box sx={{ maxWidth: 'md', mx: 'auto' }}>
          <Button variant="contained" size="large" fullWidth disabled={!valid} onClick={() => setConfirm(true)} startIcon={<Icon icon="mdi:draw-pen" width={30} />}>
            {t('huolto.signAndComplete')}
          </Button>
        </Box>
      </Box>

      <Dialog open={confirm} onClose={() => (record.isPending ? undefined : setConfirm(false))}>
        <DialogTitle sx={{ fontSize: '1.5rem' }}>{t('huolto.confirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '1.15rem' }}>
            {t('huolto.confirmText', {
              registration: o.registration,
              type: maintenanceType,
              at: formatMinutes(airframe ?? 0),
              next: formatMinutes(nextDue ?? 0),
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button size="large" onClick={() => setConfirm(false)} disabled={record.isPending}>
            {t('huolto.cancel')}
          </Button>
          <Button size="large" variant="contained" onClick={() => record.mutate()} disabled={record.isPending}>
            {t('huolto.confirmYes')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

function Step({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
      <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  )
}
